/**
 * Material Viewer Dialog - Full-Screen Overlay
 * 
 * - Video: Responsive HTML5 player with playsinline, play/pause, fullscreen
 * - Image: Lightbox with zoom (react-zoom-pan-pinch), close button
 * - PDF: Inline iframe rendering
 * - Doc: Google Docs Viewer / MS Office Online Viewer with fallback
 * - Fallback: "View in New Tab" button for unsupported types
 * 
 * Access Control:
 * - Admin: Download button visible
 * - Standard user: NO download (streaming/viewing only)
 */
import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download, ExternalLink, X, Maximize2, Minimize2,
  Play, Pause, Volume2, VolumeX, Eye, Clock,
  FileText, Video, Image as ImageIcon, File, Loader2, AlertTriangle,
} from 'lucide-react';
import { TrainingMaterial, formatFileSize } from '@/services/trainingService';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface MaterialViewerDialogProps {
  material: TrainingMaterial | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAdmin?: boolean;
}

export function MaterialViewerDialog({
  material,
  open,
  onOpenChange,
  isAdmin = false,
}: MaterialViewerDialogProps) {
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [videoFullscreen, setVideoFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when material changes
  useEffect(() => {
    setIframeError(false);
    setIframeLoading(true);
  }, [material?.id]);

  if (!material) return null;

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = material.fileUrl;
      link.download = material.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading:', error);
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
        setVideoFullscreen(true);
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
        setVideoFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'pdf': return <FileText className="h-4 w-4" />;
      case 'doc': return <FileText className="h-4 w-4" />;
      case 'image': return <ImageIcon className="h-4 w-4" />;
      default: return <File className="h-4 w-4" />;
    }
  };

  // ---- CONTENT RENDERER ----
  const renderContent = () => {
    switch (material.type) {
      case 'video':
        return (
          <div ref={containerRef} className="w-full bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              controlsList={isAdmin ? undefined : "nodownload"}
              onContextMenu={isAdmin ? undefined : (e) => e.preventDefault()}
              className="w-full rounded-lg"
              style={{ maxHeight: videoFullscreen ? '100vh' : '70vh' }}
            >
              <source src={material.fileUrl} type="video/mp4" />
              <source src={material.fileUrl} type="video/webm" />
              <source src={material.fileUrl} type="video/ogg" />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'image':
        return (
          <div className="w-full flex items-center justify-center bg-black/5 rounded-lg overflow-hidden">
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={4}
              centerOnInit
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <Button variant="secondary" size="icon" className="h-8 w-8" onClick={() => zoomIn()}>
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="secondary" size="icon" className="h-8 w-8" onClick={() => zoomOut()}>
                      <Minimize2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <TransformComponent
                    wrapperStyle={{ width: '100%', maxHeight: '70vh' }}
                    contentStyle={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <img
                      src={material.fileUrl}
                      alt={material.title}
                      className="max-w-full max-h-[70vh] object-contain"
                      onContextMenu={isAdmin ? undefined : (e) => e.preventDefault()}
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        );

      case 'pdf':
        return (
          <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading PDF...</p>
                </div>
              </div>
            )}
            <iframe
              src={`${material.fileUrl}#toolbar=${isAdmin ? 1 : 0}&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0"
              title={material.title}
              onLoad={() => setIframeLoading(false)}
              onError={() => { setIframeError(true); setIframeLoading(false); }}
            />
          </div>
        );

      case 'doc': {
        const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(material.fileUrl)}&embedded=true`;
        const msViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(material.fileUrl)}`;
        
        if (iframeError) {
          return (
            <div className="text-center py-12">
              <AlertTriangle className="h-10 w-10 text-warning mx-auto mb-3" />
              <h4 className="font-medium mb-2">Unable to preview document</h4>
              <p className="text-sm text-muted-foreground mb-4">
                The document could not be rendered in-app. Try opening it externally.
              </p>
              <div className="flex items-center justify-center gap-2">
                <Button onClick={() => window.open(googleViewerUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in Google Docs
                </Button>
                <Button variant="outline" onClick={() => window.open(material.fileUrl, '_blank')}>
                  View in New Tab
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div className="w-full h-[70vh] rounded-lg overflow-hidden relative">
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading document...</p>
                </div>
              </div>
            )}
            <iframe
              src={googleViewerUrl}
              className="w-full h-full border-0"
              title={material.title}
              onLoad={() => setIframeLoading(false)}
              onError={() => { setIframeError(true); setIframeLoading(false); }}
            />
          </div>
        );
      }

      default:
        return (
          <div className="text-center py-12">
            <File className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h4 className="font-medium mb-2">Preview not available</h4>
            <p className="text-sm text-muted-foreground mb-4">
              This file type cannot be previewed in the browser.
            </p>
            <Button onClick={() => window.open(material.fileUrl, '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View in New Tab
            </Button>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[calc(100vw-1rem)] max-h-[95dvh] p-0 gap-0 overflow-hidden">
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-card/95 backdrop-blur-sm sticky top-0 z-20">
          <div className="flex-1 min-w-0 mr-3">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                {getTypeIcon(material.type)}
              </div>
              <h3 className="font-semibold text-sm sm:text-lg truncate">{material.title}</h3>
            </div>
            {material.description && (
              <p className="text-xs text-muted-foreground truncate hidden sm:block">
                {material.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Meta badges - hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2 mr-2">
              {material.duration && (
                <Badge variant="secondary" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  {material.duration}
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                <Eye className="h-3 w-3 mr-1" />
                {material.viewCount}
              </Badge>
            </div>
            
            {/* Admin-only download */}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="h-8"
              >
                <Download className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline text-xs">Download</span>
              </Button>
            )}
            
            {/* Open in new tab */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => window.open(material.fileUrl, '_blank')}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
            
            {/* Fullscreen for video */}
            {material.type === 'video' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleFullscreen}
              >
                {videoFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
        </div>

        {/* ---- Content ---- */}
        <div className="p-3 sm:p-4 overflow-auto flex-1 relative">
          {renderContent()}
        </div>

        {/* ---- Footer: File info ---- */}
        <div className="px-4 py-2 border-t bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <span>{material.fileName}</span>
          <span>{formatFileSize(material.fileSize)}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
